// useIsMobile.js — "is this a phone-shaped viewport?", answered the same way in JS as in CSS.
//
// The app's layouts assume a wide desktop canvas: a fixed sidebar rail, report pages designed at
// 1500px, lists that shrink themselves to fit a fixed viewport height. None of that survives a
// phone, so a handful of components need to branch on the same rule the stylesheets use — and
// there must be exactly ONE definition of it, or CSS and JS disagree at the edges and the page
// half-changes shape.
//
// A phone fails the desktop layout in two different ways, so the rule has two clauses.
//
//   PORTRAIT is a WIDTH problem. 375px cannot hold a sidebar rail beside a report, and the
//   rows inside that report divide the width between a dozen columns.
//
//   LANDSCAPE is a HEIGHT problem. The same phone turned sideways is 812px wide — enough width
//   for most of the desktop layout — but only 375px tall, and every page in this app is built to
//   fit its content into exactly one viewport height rather than scroll. At 375px of height that
//   machinery (FitToPage, useFitScale, useRowFit, the zoom fits) shrinks whole reports toward
//   its own floor: everything is there, and none of it is readable.
//
// Both want the same answer — let the document scroll, and lay the page out for the room there
// actually is — so both resolve to the same "mobile" branch.
//
// The width cap on the landscape clause keeps a real desktop out of it: the widest iPhone in
// landscape is 932px, an iPad in landscape is 820px tall and so never matches at all, and a
// desktop window genuinely squashed under 500px of height is better served by scrolling than by
// a canvas fitted into nothing.
//
// The media queries in mobile.css and in each page's stylesheet spell MOBILE_QUERY out by hand
// (CSS can't read a JS constant), so if this rule ever moves, the stylesheets move with it —
// they are greppable as the exact string below.
import { useState, useEffect } from 'react';

/* An export always captures the desktop report (see desktopLayout.js), so for the length of one the
   app is told it is NOT on a phone, whatever the screen says. Module state rather than context so
   the export code can flip it without reaching into the React tree. */
let forcedDesktop = false;
const forcedListeners = new Set();

export function setForcedDesktop(on) {
  forcedDesktop = !!on;
  forcedListeners.forEach((fn) => fn(forcedDesktop));
}

export const MOBILE_MAX_WIDTH_PX = 768;
export const MOBILE_MAX_HEIGHT_PX = 500;
export const MOBILE_LANDSCAPE_MAX_WIDTH_PX = 1000;
export const MOBILE_QUERY =
  `(max-width: ${MOBILE_MAX_WIDTH_PX}px), `
  + `(max-height: ${MOBILE_MAX_HEIGHT_PX}px) and (max-width: ${MOBILE_LANDSCAPE_MAX_WIDTH_PX}px)`;

export default function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    // addListener is the pre-Safari-14 spelling; kept as a fallback so an older iOS still tracks
    // rotation rather than being stuck on whatever it measured at first paint.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const [forced, setForced] = useState(forcedDesktop);
  useEffect(() => {
    forcedListeners.add(setForced);
    setForced(forcedDesktop);
    return () => { forcedListeners.delete(setForced); };
  }, []);

  return isMobile && !forced;
}
